import { CSSResultGroup, html, LitElement, TemplateResult } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { SelectingPartialPaymentEvent } from 'components/ordering/checkout/events.js'

import {
  PARTIAL_PAYMENT_FETCHER,
  PartialPaymentFetcher
} from 'components/ordering/checkout/partial-payment/partial-payment-api.js'
import { t } from '@cloudsuite/mosaic-shared/translations/translator.js'
import { WINDOW_LOCATION, WindowLocation } from '@cloudsuite/mosaic-shared/scripts/window-location.js'
import { style } from './partial-payment.style.js'
import { inject } from '@cloudsuite/mosaic-shared/scripts/dependency-injection.js'

type PartialPaymentInfo = {
  partial_amount_formatted: string
  partial_percentage: string
}


@customElement('csc-partial-payment')
export class CSCPartialPayment extends LitElement {
  static override styles: CSSResultGroup = [style]

  @property({ type: String, attribute: 'payment-type' })
  paymentType: string = 'full'

  @property({ type: String, attribute: 'partial-payment-url' })
  partialPaymentUrl!: string

  @property({ type: Object, attribute: 'partial-payment-info' })
  partialPaymentInfo!: PartialPaymentInfo

  @inject(PARTIAL_PAYMENT_FETCHER)
  partialPaymentFetcher!: PartialPaymentFetcher

  @inject(WINDOW_LOCATION)
  protected windowLocation!: WindowLocation

  private checkedButton = (button_type: string): string => {
    if (this.paymentType === button_type) {
      return  'selected'
    } else {
      return 'selectable'
    }
  }

  private selectPartialPayment = async (payment: string): Promise<void> => {
    if (this.paymentType === payment) {
      return
    }

    this.dispatchEvent(new SelectingPartialPaymentEvent(this.paymentType))

    const formData = new FormData()
    formData.append('payment_amount', payment)

    await this.partialPaymentFetcher.selectPartialPayment(formData, this.partialPaymentUrl)
    this.windowLocation.reload()
  }

  private partialAmount = (): string => {
    if (!this.partialPaymentInfo) {
      return ''
    }
    return this.partialPaymentInfo.partial_amount_formatted
  }

  private partialPercentage = (): string => {
    if (!this.partialPaymentInfo) {
      return ''
    }
    return this.partialPaymentInfo.partial_percentage
  }

  protected override render(): TemplateResult {
    return html`
      <cse-paper>
        <cse-text variant="heading-3">
          ${t('title', {
            ns: 'csc-partial-payment',
            default: 'Choose your payment moment:'
          })}
        </cse-text>
        <div class="row">
          <cse-selectable
            fit="fill"
            state="${this.checkedButton('full')}"
            id="full"
            @click="${() => this.selectPartialPayment('full')}"
          >
            <cse-text variant="heading-6">
              ${t('full', {
                ns: 'csc-partial-payment',
                default: 'Pay total price now.'
              })}
            </cse-text>
          </cse-selectable>
          <cse-selectable
            fit="fill"
            state="${this.checkedButton('partial')}"
            id="partial"
            @click="${() => this.selectPartialPayment('partial')}"
          >
            <cse-text variant="heading-6">
              ${t('partial', {
                ns: 'csc-partial-payment',
                default: 'Pay {{variable_total_amount}} ({{variable_partial_percentage}}%)',
                variable_total_amount: this.partialAmount(),
                variable_partial_percentage: this.partialPercentage()
              })}
            </cse-text>
            <cse-text variant="caption-l">
              ${t('partial_suffix', {
                ns: 'csc-partial-payment',
                default: 'Pay {{variable_partial_percentage}}% now and the rest at delivery. Pin and cash possible.',
                variable_partial_percentage: this.partialPercentage()
              })}
            </cse-text>
          </cse-selectable>
        </div>
      </cse-paper>
    `
  }
}
